import ImageWithFallback from "@/components/ImageWithFallback";
import StatusBadge from "@/components/StatusBadge";
import { booleanOptionIconLabels } from "@/constant/GeneralOptions/boolean";
import Badge from "@/components/Badge";
import { DEFAULT_LANGUAGE_LABEL } from "@/constant/Languages/language";
import { formatDateTime } from "@/utils/helper";

export const columns = () => {
  const booleanOptionLabelsMap = booleanOptionIconLabels();

  return [
    {
      accessorKey: "status",
      header: "MPL Status",
      cell: ({ row }) => <StatusBadge status={row.getValue("status")} />,
    },
    {
      id: "show_in_profile",
      accessorFn: (row) => booleanOptionLabelsMap[row.show_in_profile],
      header: "Show Profile?",
      cell: ({ getValue }) => (
        <div className={"flex justify-center"}>{getValue()}</div>
      ),
    },
    {
      accessorKey: "history_status",
      header: `Team History Status (${DEFAULT_LANGUAGE_LABEL})`,
      cell: ({ row }) => (
        <StatusBadge status={row.getValue("history_status")} />
      ),
    },
    {
      accessorKey: "hierarchy",
      header: "Hierarchy",
      size: 60,
      cell: ({ row }) => (
        <div className={"flex justify-center"}>
          {row.original.level}.{row.original.order}
        </div>
      ),
    },
    {
      accessorKey: "sport.code",
      header: "Sport ID",
      cell: ({ row }) => (
        <div>{row.original?.sport ? row.original?.sport?.code : "-"}</div>
      ),
    },
    {
      accessorKey: "number_of_active_languages",
      header: `# Active Languages`,
      enableSorting: false,
    },
    {
      id: "default_last_change.date",
      accessorFn: (row) => formatDateTime(row.default_last_change?.date),
      header: `Last Change`,
    },
    {
      accessorKey: "default_last_change.by",
      header: `Last Change By`,
    },
    {
      accessorKey: "team_group.code",
      header: "Team Group ID",
      cell: ({ row }) => (
        <div>
          {row.original.team_group?.code ? row.original.team_group.code : "-"}
        </div>
      ),
    },
    {
      accessorKey: "code",
      header: "ID",
    },
    {
      accessorKey: "club_or_national_team",
      header: "Club / National Team",
      cell: ({ row }) => (
        <div>{row.original.is_national_team ? "National Team" : "Club"}</div>
      ),
    },
    {
      accessorKey: "city_name",
      header: "City",
      cell: ({ row }) => (
        <div>{row.original?.city ? row.original?.city_name?.name : "-"}</div>
      ),
    },
    {
      accessorKey: "country_name",
      header: "Country",
      cell: ({ row }) => (
        <div>{row.original?.country ? row.original?.country?.name : "-"}</div>
      ),
    },
    {
      accessorKey: "foundation_year",
      header: "Foundation Year",
    },
    {
      accessorKey: "stadium_name",
      header: "Stadium Name",
    },
    {
      accessorKey: "stadium_capacity",
      header: "Stadium Capacity",
    },
    {
      accessorKey: "field_length",
      header: "Field Length",
    },
    {
      accessorKey: "field_width",
      header: "Field Width",
    },
    {
      accessorKey: "anthem_name",
      header: "Antem Name",
    },
    {
      accessorKey: "mascot_1",
      header: "Mascot 1",
    },
    {
      accessorKey: "mascot_2",
      header: "Mascot 2",
    },
    {
      accessorKey: "rival_1",
      header: "Rival 1 Team",
      cell: ({ row }) => (
        <div>{row.original?.country ? row.original?.rival_1?.name : "-"}</div>
      ),
    },
    {
      accessorKey: "rival_2",
      header: "Rival 2 Team",
      cell: ({ row }) => (
        <div>{row.original?.country ? row.original?.rival_2?.name : "-"}</div>
      ),
    },
    {
      accessorKey: "rival_3",
      header: "Rival 3 Team",
      cell: ({ row }) => (
        <div>{row.original?.country ? row.original?.rival_3?.name : "-"}</div>
      ),
    },
    {
      accessorKey: "hexcode_1",
      header: "Hexcode 1",
      cell: ({ row }) =>
        row.getValue("hexcode_1") ? (
          <div
            className="w-full h-6 rounded-md"
            style={{ backgroundColor: row.getValue("hexcode_1") }}
          ></div>
        ) : (
          "-"
        ),
    },
    {
      accessorKey: "hexcode_2",
      header: "Hexcode 2",
      cell: ({ row }) =>
        row.getValue("hexcode_2") ? (
          <div
            className="w-full h-6 rounded-md"
            style={{ backgroundColor: row.getValue("hexcode_2") }}
          ></div>
        ) : (
          "-"
        ),
    },
    {
      accessorKey: "hexcode_3",
      header: "Hexcode 3",
      cell: ({ row }) =>
        row.getValue("hexcode_3") ? (
          <div
            className="w-full h-6 rounded-md"
            style={{ backgroundColor: row.getValue("hexcode_3") }}
          ></div>
        ) : (
          "-"
        ),
    },
    {
      accessorKey: "hexcode_4",
      header: "Hexcode 4",
      cell: ({ row }) =>
        row.getValue("hexcode_4") ? (
          <div
            className="w-full h-6 rounded-md"
            style={{ backgroundColor: row.getValue("hexcode_4") }}
          ></div>
        ) : (
          "-"
        ),
    },
    {
      accessorKey: "icon_url",
      header: "Icon",
      cell: ({ row }) => <ImageWithFallback url={row.getValue("icon_url")} />,
      enableResizing: false,
      enableSorting: false,
    },
    {
      id: "is_icon_bordered",
      accessorFn: (row) => booleanOptionLabelsMap[row.is_icon_bordered],
      header: "Icon Border?",
      cell: ({ getValue }) => (
        <div className={"flex justify-center"}>{getValue()}</div>
      ),
    },
    {
      accessorKey: "tags",
      header: "Team Name (Tags)",
      cell: ({ row }) => (
        <div className="flex gap-1 capitalize">
          {row.original.tags.map((tag) => (
            <Badge key={tag.id}> {tag.name}</Badge>
          ))}
        </div>
      ),
    },
    {
      accessorKey: "thumbnail_url",
      header: "Thumbnail",
      cell: ({ row }) => (
        <ImageWithFallback url={row.getValue("thumbnail_url")} />
      ),
      enableResizing: false,
      enableSorting: false,
    },
    {
      accessorKey: "banner_url",
      header: "Banner",
      cell: ({ row }) => <ImageWithFallback url={row.getValue("banner_url")} />,
      enableResizing: false,
      enableSorting: false,
    },
    {
      accessorKey: "total_likes",
      header: "Likes",
      cell: ({ row }) => <div>{row.original.likes?.total_likes || 0}</div>,
    },
    {
      accessorKey: "total_share",
      header: "Shares",
      cell: ({ row }) => <div>{row.original.shares?.total_shares || 0}</div>,
    },
    {
      accessorKey: "total_views",
      header: "Views",
      cell: ({ row }) => <div>{row.original.views?.total_views || 0}</div>,
    },
  ];
};
